/**
 * Record turnaround for one match on Match Administration (Ben,
 * 2026-10-25; the November turnaround SLO): uploaded → build started →
 * built → served, with the time between each.
 *
 * Priori staff only. The server enforces it (the endpoint answers anyone
 * else with the staff gate's 404); `staff` keeps a club account from even
 * asking. Every stage is read from a record the platform already keeps —
 * the hint below says which, and where that falls short.
 */
import { useEffect, useState } from 'react'

import { ApiError, api } from '../lib/api'
import { useActionLog } from './actionLog'
import './turnaround-panel.css'

type Stage = 'uploaded_at' | 'build_started_at' | 'built_at' | 'served_at'
type Interval = 'upload_to_build_started' | 'build_started_to_built'
  | 'built_to_served' | 'upload_to_served'
export type Turnaround = {
  game_id: number
  stages: Record<Stage, string | null>
  intervals_s: Record<Interval, number | null>
  steps_succeeded: number
  steps_required: number
}

const ROWS: { stage: Stage; label: string; since?: Interval }[] = [
  { stage: 'uploaded_at', label: 'Uploaded' },
  { stage: 'build_started_at', label: 'Build started', since: 'upload_to_build_started' },
  { stage: 'built_at', label: 'Built', since: 'build_started_to_built' },
  { stage: 'served_at', label: 'Served', since: 'built_to_served' },
]

const DASH = '—'

const when = (iso: string | null) =>
  iso ? new Date(iso).toLocaleString(undefined,
    { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : DASH

/** Whole seconds as the two largest units ("2d 4h", "3h 20m", "12m 5s",
 *  "45s"); null is a dash, never 0. Negative keeps its sign. */
export function span(s: number | null): string {
  if (s == null) return DASH
  const sign = s < 0 ? '−' : ''
  const a = Math.abs(s)
  const d = Math.floor(a / 86400), h = Math.floor((a % 86400) / 3600)
  const m = Math.floor((a % 3600) / 60), sec = a % 60
  const body = d ? `${d}d ${h}h` : h ? `${h}h ${m}m` : m ? `${m}m ${sec}s` : `${sec}s`
  return sign + body
}

const msg = (e: unknown) =>
  e instanceof ApiError || e instanceof Error ? e.message : String(e)

export function TurnaroundPanel({ gameId, staff }: { gameId: number; staff: boolean }) {
  const { stackVersion } = useActionLog()
  const [data, setData] = useState<Turnaround | null>(null)
  const [error, setError] = useState<string | null>(null)

  // stackVersion moves after any logged action (a build queued, serving
  // switched), so the stages follow what was just done.
  useEffect(() => {
    if (!staff) return
    let live = true
    api<Turnaround>(`/api/v1/opsqa/games/${gameId}/turnaround`)
      .then((d) => { if (live) { setData(d); setError(null) } })
      .catch((e: unknown) => { if (live) setError(msg(e)) })
    return () => { live = false }
  }, [gameId, staff, stackVersion])

  if (!staff) return null

  return (
    <section className="ls-tat" data-testid="turnaround">
      <div className="ls-tat__head">
        <span className="t-label">Record turnaround</span>
        <span className="muted">Priori staff only</span>
      </div>
      {error && <p className="error">Turnaround could not be read — {error}</p>}
      {!error && !data && <p className="muted">Loading…</p>}
      {data && (
        <>
          <table className="ls-tat__table">
            <thead>
              <tr><th>Stage</th><th>When</th><th>Since previous stage</th></tr>
            </thead>
            <tbody>
              {ROWS.map((r) => (
                <tr key={r.stage} data-stage={r.stage}>
                  <th scope="row">{r.label}</th>
                  <td>
                    {when(data.stages[r.stage])}
                    {r.stage === 'built_at' && !data.stages.built_at && data.steps_succeeded > 0 && (
                      <span className="muted">
                        {' '}({data.steps_succeeded} of {data.steps_required} steps have succeeded)
                      </span>
                    )}
                  </td>
                  <td>{r.since ? span(data.intervals_s[r.since]) : ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="ls-tat__total">
            Upload to served: <strong>{span(data.intervals_s.upload_to_served)}</strong>
          </p>
          <p className="muted ls-tat__hint">
            Uploaded: when the match&apos;s tracking file was registered. Build started: when a
            worker first began any build step. Built: when the last of the {data.steps_required}{' '}
            per-match build steps first succeeded — a later rebuild does not move it, and a step
            that has never succeeded leaves it empty. Served: the last time serving was switched
            on, not the first. Builds run from the command line are not recorded. A negative
            time means the later stage was recorded first.
          </p>
        </>
      )}
    </section>
  )
}
